import React, { useState, useMemo } from 'react';
import { PageLayout } from '../components/page-layout';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  LinearProgress,
  Button,
  Stack,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import ShieldIcon from '@mui/icons-material/Shield';
import GroupsIcon from '@mui/icons-material/Groups';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import { TEAM_PROJECTIONS_DATA, POSITION_COLORS } from '../data/team-projections-data';

export const TeamProjectionsPage = () => {
  const [selectedClubTab, setSelectedClubTab] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('ALL');

  // Top score in the league for progress bar scaling
  const maxTeamPts = useMemo(() => {
    return Math.max(...TEAM_PROJECTIONS_DATA.map((t) => t.totalPts));
  }, []);

  // Compute all-player list for global search / insights
  const allPlayersWithClub = useMemo(() => {
    const list = [];
    TEAM_PROJECTIONS_DATA.forEach((club, clubIndex) => {
      club.players.forEach((player) => {
        list.push({
          ...player,
          clubId: club.clubId,
          clubName: club.clubName,
          managerName: club.managerName,
          clubRank: clubIndex + 1,
        });
      });
    });
    return list;
  }, []);

  // Top 10 players overall
  const top10Players = useMemo(() => {
    return [...allPlayersWithClub]
      .filter((p) => p.matched)
      .sort((a, b) => b.pts - a.pts)
      .slice(0, 10);
  }, [allPlayersWithClub]);

  // Position breakdown helper per club
  const getClubPosBreakdown = (players) => {
    const posSums = { GK: 0, DEF: 0, MID: 0, FWD: 0 };
    players.forEach((p) => {
      const pos = p.position || p.fplPos;
      if (posSums[pos] !== undefined) {
        posSums[pos] += p.pts;
      }
    });
    return {
      GK: Math.round(posSums.GK * 10) / 10,
      DEF: Math.round(posSums.DEF * 10) / 10,
      MID: Math.round(posSums.MID * 10) / 10,
      FWD: Math.round(posSums.FWD * 10) / 10,
    };
  };

  // Filtered clubs & players
  const filteredClubs = useMemo(() => {
    return TEAM_PROJECTIONS_DATA.filter((club) => {
      if (selectedClubTab !== 'ALL' && club.clubId !== selectedClubTab) {
        return false;
      }
      return true;
    }).map((club) => {
      const filteredPlayers = club.players.filter((p) => {
        const matchesSearch =
          searchQuery.trim() === '' ||
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.webName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.fplTeam.toLowerCase().includes(searchQuery.toLowerCase()) ||
          club.clubName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          club.managerName.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesPos =
          selectedPosition === 'ALL' ||
          p.position === selectedPosition ||
          p.fplPos === selectedPosition;

        return matchesSearch && matchesPos;
      });

      return {
        ...club,
        filteredPlayers,
      };
    });
  }, [selectedClubTab, searchQuery, selectedPosition]);

  const getRankBadge = (index) => {
    if (index === 0) return { icon: '🥇', label: '1st', color: '#f1c40f', bg: '#fef9e7' };
    if (index === 1) return { icon: '🥈', label: '2nd', color: '#95a5a6', bg: '#f2f4f4' };
    if (index === 2) return { icon: '🥉', label: '3rd', color: '#e67e22', bg: '#fdf2e9' };
    return { icon: `#${index + 1}`, label: `${index + 1}th`, color: '#555', bg: '#f8f9fa' };
  };

  return (
    <PageLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, margin: '0 auto' }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
            <EmojiEventsIcon sx={{ fontSize: 38, color: '#f1c40f' }} />
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1a365d' }}>
              Pre-Season Team FPL Projections
            </Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">
            Projected Fantasy Premier League (FPL) points for all 13 official Tlog clubs and 143 drafted players based on pre-season statistical projection models.
          </Typography>
        </Box>

        {/* Top Summary Metric Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fef9e7', border: '1px solid #f9e79f', height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary" fontWeight="bold">
                    🥇 Predicted Champion
                  </Typography>
                  <EmojiEventsIcon sx={{ color: '#f1c40f' }} />
                </Stack>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#b7950b', mt: 0.5 }}>
                  {TEAM_PROJECTIONS_DATA[0].clubName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{TEAM_PROJECTIONS_DATA[0].totalPts.toFixed(1)}</strong> projected pts ({TEAM_PROJECTIONS_DATA[0].managerName})
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ebf5fb', border: '1px solid #aed6f1', height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary" fontWeight="bold">
                    🥈 Runner Up
                  </Typography>
                  <TrendingUpIcon sx={{ color: '#3498db' }} />
                </Stack>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#2980b9', mt: 0.5 }}>
                  {TEAM_PROJECTIONS_DATA[1].clubName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{TEAM_PROJECTIONS_DATA[1].totalPts.toFixed(1)}</strong> pts ({Math.round((TEAM_PROJECTIONS_DATA[0].totalPts - TEAM_PROJECTIONS_DATA[1].totalPts) * 10) / 10} pts behind)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#eafaf1', border: '1px solid #a9dfbf', height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary" fontWeight="bold">
                    ⭐ Top Projected Player
                  </Typography>
                  <StarIcon sx={{ color: '#27ae60' }} />
                </Stack>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e8449', mt: 0.5 }}>
                  {top10Players[0]?.name || 'Erling Haaland'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>{top10Players[0]?.pts.toFixed(1)} pts</strong> ({top10Players[0]?.clubName})
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fdf2e9', border: '1px solid #f5cba7', height: '100%' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" color="text.secondary" fontWeight="bold">
                    👥 League Scope
                  </Typography>
                  <GroupsIcon sx={{ color: '#e67e22' }} />
                </Stack>
                <Typography variant="h5" fontWeight="bold" sx={{ color: '#d35400', mt: 0.5 }}>
                  13 Clubs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  143 players drafted (11 per club)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section 1: League Leaderboard Table */}
        <Paper sx={{ mb: 4, p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2c3e50' }}>
                🏆 Predicted League Standings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ranked by total projected FPL points across all 11 drafted players
              </Typography>
            </Box>
          </Stack>

          <TableContainer>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead sx={{ bgcolor: '#f4f6f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '70px', textAlign: 'center' }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Club & Manager</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Projected Pts</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>Relative Index</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Positional Breakdown (GK / DEF / MID / FWD)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Top Projected Scorer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {TEAM_PROJECTIONS_DATA.map((club, index) => {
                  const rank = getRankBadge(index);
                  const posBreakdown = getClubPosBreakdown(club.players);
                  const topPlayer = [...club.players].sort((a, b) => b.pts - a.pts)[0];
                  const percentOfMax = (club.totalPts / maxTeamPts) * 100;

                  return (
                    <TableRow
                      key={club.clubId}
                      hover
                      sx={{
                        bgcolor: selectedClubTab === club.clubId ? '#edf7ed' : (index < 3 ? rank.bg : 'inherit'),
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {rank.icon}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="bold" sx={{ color: '#1a365d' }}>
                            {club.clubName}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {club.managerName}
                            </Typography>
                          </Stack>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.05rem', color: '#1a365d' }}>
                        {club.totalPts.toFixed(1)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%', mr: 1 }}>
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {percentOfMax.toFixed(0)}% of leader
                            </Typography>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={percentOfMax}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: '#e2e8f0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: index === 0 ? '#f1c40f' : index < 3 ? '#3498db' : '#2ecc71',
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title={`Goalkeeper: ${posBreakdown.GK} pts`}>
                            <Chip size="small" label={`GK: ${posBreakdown.GK}`} sx={{ bgcolor: '#fdf2e9', color: '#d35400', fontSize: '0.72rem' }} />
                          </Tooltip>
                          <Tooltip title={`Defenders: ${posBreakdown.DEF} pts`}>
                            <Chip size="small" label={`DEF: ${posBreakdown.DEF}`} sx={{ bgcolor: '#eafaf1', color: '#27ae60', fontSize: '0.72rem' }} />
                          </Tooltip>
                          <Tooltip title={`Midfielders: ${posBreakdown.MID} pts`}>
                            <Chip size="small" label={`MID: ${posBreakdown.MID}`} sx={{ bgcolor: '#ebf5fb', color: '#2980b9', fontSize: '0.72rem' }} />
                          </Tooltip>
                          <Tooltip title={`Forwards: ${posBreakdown.FWD} pts`}>
                            <Chip size="small" label={`FWD: ${posBreakdown.FWD}`} sx={{ bgcolor: '#fdedec', color: '#c0392b', fontSize: '0.72rem' }} />
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {topPlayer ? (
                          <Typography variant="body2">
                            <strong>{topPlayer.name}</strong> ({topPlayer.pts.toFixed(1)} pts)
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Button
                          size="small"
                          variant={selectedClubTab === club.clubId ? 'contained' : 'outlined'}
                          color="primary"
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            setSelectedClubTab(club.clubId);
                            window.scrollTo({ top: 650, behavior: 'smooth' });
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Section 2: Detailed Club Squads Breakdown */}
        <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, mb: 4 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#2c3e50', mb: 2 }}>
            📋 Detailed Club Rosters & Player Projections
          </Typography>

          {/* Club Filter Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={selectedClubTab}
              onChange={(e, val) => setSelectedClubTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ '& .MuiTab-root': { fontWeight: 'bold', minWidth: 100 } }}
            >
              <Tab label="All Clubs" value="ALL" />
              {TEAM_PROJECTIONS_DATA.map((club, idx) => (
                <Tab
                  key={club.clubId}
                  value={club.clubId}
                  label={`#${idx + 1} ${club.clubName}`}
                />
              ))}
            </Tabs>
          </Box>

          {/* Filter and Search Bar */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 3 }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              size="small"
              placeholder="Search player, club, or manager..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: { xs: '100%', sm: 340 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                Position:
              </Typography>
              {['ALL', 'GK', 'DEF', 'MID', 'FWD'].map((pos) => (
                <Button
                  key={pos}
                  size="small"
                  variant={selectedPosition === pos ? 'contained' : 'outlined'}
                  onClick={() => setSelectedPosition(pos)}
                  sx={{
                    minWidth: 50,
                    textTransform: 'none',
                    bgcolor: selectedPosition === pos && pos !== 'ALL' ? POSITION_COLORS[pos] : undefined,
                    borderColor: pos !== 'ALL' ? POSITION_COLORS[pos] : undefined,
                    color: selectedPosition !== pos && pos !== 'ALL' ? POSITION_COLORS[pos] : undefined,
                    '&:hover': {
                      bgcolor: selectedPosition === pos && pos !== 'ALL' ? POSITION_COLORS[pos] : undefined,
                    },
                  }}
                >
                  {pos}
                </Button>
              ))}
            </Stack>
          </Stack>

          {/* Clubs Display */}
          <Stack spacing={4}>
            {filteredClubs.map((club) => {
              const originalIndex = TEAM_PROJECTIONS_DATA.findIndex((t) => t.clubId === club.clubId);
              const rank = getRankBadge(originalIndex);
              const posBreakdown = getClubPosBreakdown(club.players);

              // Sum filtered stats for total row
              const totalMins = club.filteredPlayers.reduce((acc, p) => acc + p.mins, 0);
              const totalGoals = club.filteredPlayers.reduce((acc, p) => acc + p.goals, 0);
              const totalAssists = club.filteredPlayers.reduce((acc, p) => acc + p.assists, 0);
              const totalCS = club.filteredPlayers.reduce((acc, p) => acc + p.cleanSheets, 0);
              const totalBonus = club.filteredPlayers.reduce((acc, p) => acc + p.bonus, 0);
              const totalYC = club.filteredPlayers.reduce((acc, p) => acc + p.yc, 0);
              const totalFilteredPts = club.filteredPlayers.reduce((acc, p) => acc + p.pts, 0);

              return (
                <Card
                  key={club.clubId}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    borderColor: originalIndex === 0 ? '#f1c40f' : '#e2e8f0',
                  }}
                >
                  {/* Club Card Header */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: originalIndex === 0 ? '#fef9e7' : '#f8fafc',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ sm: 'center' }}
                      spacing={1}
                    >
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a365d' }}>
                            {club.clubName}
                          </Typography>
                          <Chip
                            label={`Rank ${rank.label} ${rank.icon}`}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              bgcolor: rank.bg,
                              border: `1px solid ${rank.color}`,
                              color: rank.color === '#555' ? '#2d3748' : rank.color,
                            }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                          Manager: <strong>{club.managerName}</strong> &bull; {club.projectedCount} / {club.playersCount} players projected
                        </Typography>
                      </Box>

                      <Stack direction="row" spacing={2} alignItems="center">
                        <Stack direction="row" spacing={0.5}>
                          <Chip size="small" label={`GK ${posBreakdown.GK}`} sx={{ bgcolor: '#fdf2e9', color: '#d35400', fontSize: '0.75rem' }} />
                          <Chip size="small" label={`DEF ${posBreakdown.DEF}`} sx={{ bgcolor: '#eafaf1', color: '#27ae60', fontSize: '0.75rem' }} />
                          <Chip size="small" label={`MID ${posBreakdown.MID}`} sx={{ bgcolor: '#ebf5fb', color: '#2980b9', fontSize: '0.75rem' }} />
                          <Chip size="small" label={`FWD ${posBreakdown.FWD}`} sx={{ bgcolor: '#fdedec', color: '#c0392b', fontSize: '0.75rem' }} />
                        </Stack>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Club Projected Total
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" sx={{ color: '#27ae60' }}>
                            {club.totalPts.toFixed(1)} <Typography component="span" variant="body2">pts</Typography>
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Players Table */}
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#fafafa' }}>
                        <TableRow>
                          <TableCell sx={{ width: '40px', fontWeight: 'bold' }}>#</TableCell>
                          <TableCell sx={{ width: '70px', fontWeight: 'bold' }}>Pos</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Player Name</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>FPL Club</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>FPL Price</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Proj. Mins</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>G</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>A</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>CS</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Bonus</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>YC</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#1a365d' }}>Projected Pts</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Value (Pts/£m)</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {club.filteredPlayers.map((player, pIdx) => {
                          const posColor = POSITION_COLORS[player.position] || '#555';

                          return (
                            <TableRow
                              key={player.playerId || player.name + pIdx}
                              hover
                              sx={{
                                bgcolor: !player.matched ? '#fffaf9' : 'inherit',
                              }}
                            >
                              <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{pIdx + 1}</TableCell>
                              <TableCell>
                                <Chip
                                  label={player.position}
                                  size="small"
                                  sx={{
                                    bgcolor: posColor,
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '0.72rem',
                                    height: 22,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: '#2d3748' }}>
                                {player.name}
                              </TableCell>
                              <TableCell>
                                <Chip label={player.fplTeam} size="small" variant="outlined" sx={{ fontSize: '0.72rem', height: 20 }} />
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.fplPrice > 0 ? `£${player.fplPrice.toFixed(1)}m` : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.mins > 0 ? Math.round(player.mins) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.goals > 0 ? player.goals.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.assists > 0 ? player.assists.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.cleanSheets > 0 ? player.cleanSheets.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.bonus > 0 ? player.bonus.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {player.yc > 0 ? player.yc.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell
                                sx={{
                                  textAlign: 'right',
                                  fontWeight: 'bold',
                                  fontSize: '0.95rem',
                                  color: player.pts > 150 ? '#27ae60' : player.pts > 100 ? '#2980b9' : player.pts > 0 ? '#2c3e50' : '#95a5a6',
                                }}
                              >
                                {player.pts > 0 ? player.pts.toFixed(1) : '0.0'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'right', color: 'text.secondary' }}>
                                {player.value > 0 ? player.value.toFixed(1) : '-'}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center' }}>
                                {player.matched ? (
                                  <Chip label="Matched" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                ) : (
                                  <Chip label="Unprojected" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                        {/* Club Total Footer Row */}
                        <TableRow sx={{ bgcolor: '#f8fafc', '& td': { fontWeight: 'bold', borderTop: '2px solid #cbd5e1' } }}>
                          <TableCell colSpan={5} sx={{ textAlign: 'right' }}>
                            {club.clubName} Subtotals:
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{Math.round(totalMins)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{totalGoals.toFixed(1)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{totalAssists.toFixed(1)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{totalCS.toFixed(1)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{totalBonus.toFixed(1)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>{totalYC.toFixed(1)}</TableCell>
                          <TableCell sx={{ textAlign: 'right', color: '#27ae60', fontSize: '1.05rem' }}>
                            {totalFilteredPts.toFixed(1)}
                          </TableCell>
                          <TableCell colSpan={2} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              );
            })}
          </Stack>
        </Paper>

        {/* Section 3: League Highlights & Top Performers */}
        <Grid container spacing={3}>
          {/* Top 10 Projected Players Overall */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#2c3e50', mb: 2 }}>
                ⭐ Top 10 Projected Players League-Wide
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f4f6f9' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Pos</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Fantasy Club</TableCell>
                      <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>Projected Pts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top10Players.map((player, idx) => (
                      <TableRow key={player.playerId || player.name + idx} hover>
                        <TableCell sx={{ fontWeight: 'bold', color: idx < 3 ? '#f1c40f' : 'inherit' }}>
                          #{idx + 1}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{player.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={player.position}
                            size="small"
                            sx={{
                              bgcolor: POSITION_COLORS[player.position] || '#555',
                              color: '#fff',
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip label={player.clubName} size="small" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', color: '#27ae60' }}>
                          {player.pts.toFixed(1)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Positional Power Rankings */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#2c3e50', mb: 2 }}>
                📊 Top Performing Units by Position
              </Typography>

              <Stack spacing={2.5}>
                {/* Best Defense */}
                <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 1.5, border: '1px solid #bbf7d0' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ShieldIcon sx={{ color: '#16a34a' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" color="#166534">
                        🛡️ Top Projected Defense: Real MaQuiz Athletic FC (629.8 pts)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Virgil (154.9), Lacroix (136.5), Truffert (133.4), Van Hecke (128.3), Bart Verbruggen (117.9)
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Best Midfield */}
                <Box sx={{ p: 2, bgcolor: '#eff6ff', borderRadius: 1.5, border: '1px solid #bfdbfe' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SportsSoccerIcon sx={{ color: '#2563eb' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" color="#1e40af">
                        🎯 Top Projected Midfield: Rick Wallers Athletic? (745.3 pts)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cole Palmer (207.1), Bryan Mbeumo (182.4), Florian Wirtz (155.1), Harvey Barnes (102.8), Estêvão (41.0)
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Best Attack */}
                <Box sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fecaca' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <EmojiEventsIcon sx={{ color: '#dc2626' }} />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" color="#991b1b">
                        ⚡ Top Projected Attack: Ladyboy Loving Losers (248.0 pts)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ollie Watkins (144.3), Igor Jesus (103.7)
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PageLayout>
  );
};
